import React from 'react';
import { Head, useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Save, ArrowLeft, AlertCircle, Upload, File } from 'lucide-react';
import { Link as InertiaLink } from '@inertiajs/react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import DestinationCheckboxes, {
  submittedDestinations,
  type DestinationSelection,
} from '@/components/documents/DestinationCheckboxes';
import MunicipalityLinkFields, {
  municipalitiesForDocumentType,
  type MunicipalityLinkSelection,
  type MunicipalityOption,
} from '@/components/documents/MunicipalityLinkFields';
import { type UploadConfig, uploadConfigFor } from '@/lib/fileLibraryUploadConfig';

interface Document {
  id?: number;
  name: string;
  description: string;
  file_path?: string;
  status?: string;
  vectorized_status?: string;
  update_cadence?: string;
  document_type?: string;
  meeting_date?: string;
  state_abbreviation?: string;
  user_name?: string;
}

interface DocumentFormProps {
  document: Document;
  isEdit: boolean;
  errors: Record<string, string[]>;
  stateOptions: string[];
  fileLibraryUpload: UploadConfig;
  municipalities?: MunicipalityOption[];
}

function formFieldsFromLinks(links: MunicipalityLinkSelection[]) {
  return {
    municipality_ids: links.map((link) => link.municipalityId),
    municipality_document_group_ids: Object.fromEntries(
      links
        .filter((link) => link.folderId)
        .map((link) => [String(link.municipalityId), link.folderId]),
    ),
  };
}

function destinationSelection(data: DestinationSelection): DestinationSelection {
  return {
    file_center: data.file_center,
    connect: data.connect,
    research_assistant: data.research_assistant,
  };
}

function linksFromFormFields(
  municipalityIds: number[],
  folderIds: Record<string, string>,
): MunicipalityLinkSelection[] {
  return municipalityIds.map((municipalityId) => ({
    municipalityId,
    folderId: folderIds[String(municipalityId)] || '',
  }));
}

export default function DocumentForm({ document, isEdit, errors, stateOptions, fileLibraryUpload, municipalities = [] }: DocumentFormProps) {
  const initialDestinations = submittedDestinations();
  const { data, setData, post, put, processing, errors: formErrors } = useForm({
    name: document.name || '',
    description: document.description || '',
    file: null as File | null,
    update_cadence: document.update_cadence || '',
    document_type: document.document_type || 'hierarchical',
    meeting_date: document.meeting_date || '',
    state_abbreviation: document.state_abbreviation || '',
    user_name: document.user_name || '',
    municipality_ids: [] as number[],
    municipality_document_group_ids: {} as Record<string, string>,
    ...initialDestinations,
  });

  const requiresState = data.document_type === 'current_state_law' || data.document_type === 'potential_state_law';
  const fileUploadConfig = uploadConfigFor(data.document_type, fileLibraryUpload);

  const allErrors: Record<string, string | string[] | undefined> = { ...errors, ...formErrors };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit && document.id) {
      // For updates, use PUT method
      put(`/documents/${document.id}`, {
        onSuccess: () => {
          // Handle success - Inertia will handle the redirect
        },
      });
    } else {
      // For create, use POST method
      post('/documents', {
        onSuccess: () => {
          // Handle success - Inertia will handle the redirect
        },
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      uploaded: 'bg-orange-100 text-orange-800',
      creating_chunks: 'bg-orange-100 text-orange-800',
      vectorizing: 'bg-blue-100 text-blue-800',
      vectorize_completed: 'bg-green-100 text-green-800',
      vectorize_failed: 'bg-red-100 text-red-800'
    } as const;

    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const getProgressValue = (status: string) => {
    const progress = {
      pending: 0,
      uploaded: 25,
      processing: 50,
      completed: 100,
      failed: 0,
      creating_chunks: 30,
      vectorizing: 70,
      vectorize_completed: 100,
      vectorize_failed: 0
    } as const;

    return progress[status as keyof typeof progress] || 0;
  };

  const hasAnyErrors = ['name', 'description', 'file', 'update_cadence'].some(key =>
    (allErrors[key as keyof typeof allErrors]?.length || 0) > 0
  );

  return (
    <>
      <Head title={isEdit ? `Edit ${document.name}` : 'New Document'} />

      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: isEdit ? document.name : 'New Document', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center gap-4">
            <Button asChild variant="outline">
              <InertiaLink href="/documents">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Documents
              </InertiaLink>
            </Button>
            <h1 className="text-3xl font-bold">
              {isEdit ? `Edit ${document.name}` : 'New Document'}
            </h1>
          </div>

          {/* Error Summary */}
          {hasAnyErrors && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">
                  {['name', 'description', 'file'].reduce((count, key) =>
                    count + (allErrors[key as keyof typeof allErrors]?.length || 0), 0
                  )} error(s) prohibited this document from being saved:
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {['name', 'description', 'file'].map(field => {
                    const fieldErrors = allErrors[field as keyof typeof allErrors];
                    if (!fieldErrors || fieldErrors.length === 0) return null;
                    return Array.isArray(fieldErrors)
                      ? fieldErrors.map((error: string, index: number) => (
                          <li key={`${field}-${index}`}>{error}</li>
                        ))
                      : <li key={field}>{fieldErrors}</li>;
                  })}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Status Information (Edit only) */}
                {isEdit && document.status && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold text-lg mb-4">Document Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-semibold">Document Status:</span>
                          <Badge className={getStatusBadge(document.status)}>
                            {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
                          </Badge>
                        </div>
                        <Progress value={getProgressValue(document.status)} className="h-2" />
                      </div>

                      {document.vectorized_status && (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold">Vectorized Status:</span>
                            <Badge className={getStatusBadge(document.vectorized_status)}>
                              {document.vectorized_status.replace('_', ' ').charAt(0).toUpperCase() +
                               document.vectorized_status.replace('_', ' ').slice(1)}
                            </Badge>
                          </div>
                          <Progress value={getProgressValue(document.vectorized_status)} className="h-2" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Basic Fields */}
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={data.name}
                      onChange={(e) => setData('name', e.target.value)}
                      className={allErrors.name ? 'border-red-500' : ''}
                      required
                    />
                    {allErrors.name && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.name) ? allErrors.name.join(', ') : allErrors.name}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium">
                      Description
                    </Label>
                    <Textarea
                      id="description"
                      value={data.description}
                      onChange={(e) => setData('description', e.target.value)}
                      rows={6}
                      className={allErrors.description ? 'border-red-500' : ''}
                    />
                    {allErrors.description && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.description) ? allErrors.description.join(', ') : allErrors.description}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user_name" className="text-sm font-medium">
                      Uploaded By
                    </Label>
                    <Input
                      id="user_name"
                      type="text"
                      value={data.user_name}
                      onChange={(e) => setData('user_name', e.target.value)}
                      className={allErrors.user_name ? 'border-red-500' : ''}
                      placeholder="Enter your name"
                    />
                    {allErrors.user_name && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.user_name) ? allErrors.user_name.join(', ') : allErrors.user_name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Update Cadence */}
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="update_cadence" className="text-sm font-medium">
                      Update Cadence
                    </Label>
                    <select
                      id="update_cadence"
                      value={data.update_cadence}
                      onChange={(e) => setData('update_cadence', e.target.value)}
                      className={`border rounded-md p-2 ${allErrors.update_cadence ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select cadence</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    {allErrors.update_cadence && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.update_cadence) ? allErrors.update_cadence.join(', ') : allErrors.update_cadence}
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Type */}
                <div className="grid grid-cols-1 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="document_type" className="text-sm font-medium">
                      Document Type *
                    </Label>
                    <select
                      id="document_type"
                      value={data.document_type}
                      onChange={(e) => {
                        const documentType = e.target.value;
                        const allowed = municipalitiesForDocumentType(municipalities, documentType);
                        const nextLinks = linksFromFormFields(data.municipality_ids, data.municipality_document_group_ids)
                          .filter((link) => (allowed ?? []).some((municipality) => municipality.id === link.municipalityId));
                        setData({
                          ...data,
                          document_type: documentType,
                          ...(isEdit ? {} : formFieldsFromLinks(nextLinks)),
                          ...(isEdit ? {} : submittedDestinations()),
                        });
                      }}
                      className={`border rounded-md p-2 w-full ${allErrors.document_type ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="hierarchical">Hierarchical (Ordinances, Codes, Standards)</option>
                      <option value="meeting_minutes">Meeting Minutes</option>
                      <option value="current_state_law">Current State Law</option>
                      <option value="potential_state_law">Potential State Law</option>
                      <option value="uploaded_file">Uploaded file</option>
                    </select>
                    {allErrors.document_type && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.document_type) ? allErrors.document_type.join(', ') : allErrors.document_type}
                      </div>
                    )}
                  </div>
                </div>

                {/* Meeting Date (only show for meeting_minutes type) */}
                {data.document_type === 'meeting_minutes' && (
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="meeting_date" className="text-sm font-medium">
                        Meeting Date *
                      </Label>
                      <Input
                        id="meeting_date"
                        type="date"
                        value={data.meeting_date}
                        onChange={(e) => setData('meeting_date', e.target.value)}
                        className={allErrors.meeting_date ? 'border-red-500' : ''}
                        required
                      />
                      {allErrors.meeting_date && (
                        <div className="text-sm text-red-600">
                          {Array.isArray(allErrors.meeting_date) ? allErrors.meeting_date.join(', ') : allErrors.meeting_date}
                        </div>
                      )}
                      <div className="text-xs text-gray-600">
                        The date when this meeting took place. This will be used for temporal filtering when searching meeting minutes.
                      </div>
                    </div>
                  </div>
                )}

                {!isEdit && (
                  <div className="space-y-2">
                    <MunicipalityLinkFields
                      municipalities={municipalities}
                      documentType={data.document_type}
                      links={linksFromFormFields(data.municipality_ids, data.municipality_document_group_ids)}
                      onChange={(links) => setData({ ...data, ...formFieldsFromLinks(links) })}
                    />
                    {allErrors.base && (
                      <div className="text-sm text-red-600">
                        {Array.isArray(allErrors.base) ? allErrors.base.join(', ') : allErrors.base}
                      </div>
                    )}
                    <DestinationCheckboxes
                      documentType={data.document_type}
                      value={destinationSelection(data)}
                      onChange={(destinations) => setData({ ...data, ...destinations })}
                    />
                  </div>
                )}

                {requiresState && (
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="state_abbreviation" className="text-sm font-medium">
                        State Abbreviation *
                      </Label>
                      <select
                        id="state_abbreviation"
                        value={data.state_abbreviation}
                        onChange={(e) => setData('state_abbreviation', e.target.value)}
                        className={`border rounded-md p-2 w-full ${allErrors.state_abbreviation ? 'border-red-500' : ''}`}
                        required
                      >
                        <option value="">Select state</option>
                        {stateOptions.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      {allErrors.state_abbreviation && (
                        <div className="text-sm text-red-600">
                          {Array.isArray(allErrors.state_abbreviation) ? allErrors.state_abbreviation.join(', ') : allErrors.state_abbreviation}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* File Upload/Display */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    {document.file_path ? (
                      <>
                        <File className="h-4 w-4" />
                        Uploaded File
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload File
                      </>
                    )}
                  </Label>

                  {document.file_path ? (
                    <div className="p-3 border rounded-md bg-gray-50">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">
                          {document.file_path.split('/').pop()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Input
                        id="file"
                        type="file"
                        accept={fileUploadConfig.accept}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          setData('file', file || null);
                        }}
                        className={allErrors.file ? 'border-red-500' : ''}
                      />
                      <div className="text-xs text-gray-600">
                        {fileUploadConfig.help}
                      </div>
                    </>
                  )}

                  {allErrors.file && (
                    <div className="text-sm text-red-600">
                      {Array.isArray(allErrors.file) ? allErrors.file.join(', ') : allErrors.file}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <Button
                    type="submit"
                    disabled={processing}
                    size="lg"
                    className="px-8"
                  >
                    {processing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {isEdit ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isEdit ? 'Update Document' : 'Create Document'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
